import { FC, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Spinner from '../../atoms/Spinner/Spinner';
import UserManagementUsersEditRolesTemplate from '../../templates/UserManagementUsersEditRolesTemplate/UserManagementUsersEditRolesTemplate';
import { AddRoleModal } from '../../organisms';
import { UserRoleSaveRequestDto } from '../../../store/contextAPI/user/dto/request/user-role-save-request.dto';
import { DEFAULT_CONTEXT_ID, getUUID } from '../../../utils/UUID';
import { showError } from '../../../utils/Notifications';
import {
  useGetRoles,
  useGetUserById,
} from '../../../store/contextAPI/user/queries/user.queries';
import { Role } from '../../../store/contextAPI/role/dto/response/role.dto';
import userRoleService from '../../../store/contextAPI/user/services/user-role.service';
import {
  useGetBuyers,
  useGetConsortiums,
  useGetConveners,
} from '../../../store/buyerAPI/buyer/queries/buyer.queries';
import { useGetLandscapes } from '../../../store/landscapeAPI/landscape/queries/landscape.queries';
import { BasicInfo } from '../../../utils/types/types';

const UserManagementUsersEditDetailsPage: FC = () => {
  const { id } = useParams();
  const clientQuery = useQueryClient();

  const { data: user, isLoading } = useGetUserById(id || '');
  const { data: roles, isLoading: areRolesLoading } = useGetRoles();

  const handleDeleteRole = async (roleId: string) => {
    await userRoleService.deleteUserRole(roleId);
  };

  const { mutate: deleteRole, isPending: isPendingDeleteRole } = useMutation({
    mutationFn: handleDeleteRole,
    onSuccess: () =>
      clientQuery.invalidateQueries({
        queryKey: ['user', user?.id],
      }),
    onError: (error) => console.log(error),
  });

  // =============================================================================
  // ************************************MODAL************************************
  // =============================================================================

  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedBuyer, setSelectedBuyer] = useState<BasicInfo>();
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [selectedLandscape, setSelectedLandscape] = useState<BasicInfo>();

  const { data: buyers } = useGetBuyers();
  const { data: conveners } = useGetConveners();
  const { data: consortiums } = useGetConsortiums();
  const { data: landscapes } = useGetLandscapes();

  const addRole = async () => {
    if (!selectedRole) {
      showError('Please select a role!');
      return;
    }

    let contextId = DEFAULT_CONTEXT_ID;
    if (selectedRole.name === 'LandscapeInitiativeManager') {
      if (!selectedLandscape) {
        showError('Please select a landscape!');
        return;
      }

      contextId = selectedLandscape.id;
    } else if (
      selectedRole.name === 'BuyerManager' ||
      selectedRole.name === 'ConsortiumManager' ||
      selectedRole.name === 'ConvenerManager'
    ) {
      if (!selectedBuyer) {
        showError('Please select an organization!');
        return;
      }
      contextId = selectedBuyer.id;
    }

    const request: UserRoleSaveRequestDto = {
      contextId: contextId,
      id: getUUID(),
      published: true,
      roleId: selectedRole.id,
      userId: user!.userId || '',
    };

    await userRoleService.createUserRole(request);

    setSelectedRole(undefined);
    setSelectedBuyer(undefined);
    setSelectedLandscape(undefined);
  };

  const { mutate: handleAddRole, isPending: isPendingAddRole } = useMutation({
    mutationFn: addRole,
    onSuccess: () => {
      setShowModal(false);
      clientQuery.invalidateQueries({
        queryKey: ['user', user?.id],
      });
    },
    onError: (error) => console.log(error.message),
  });

  if (isLoading || areRolesLoading || isPendingAddRole || isPendingDeleteRole) {
    return <Spinner />;
  }

  return (
    <>
      {showModal && (
        <AddRoleModal
          show={showModal}
          setShow={setShowModal}
          roles={roles ?? []}
          onSelect={setSelectedRole}
          addRole={handleAddRole}
          landscapes={landscapes ?? []}
          buyers={buyers ?? []}
          conveners={conveners ?? []}
          consortiums={consortiums ?? []}
          onSelectLandscape={setSelectedLandscape}
          onSelectBuyer={setSelectedBuyer}
        />
      )}
      <UserManagementUsersEditRolesTemplate
        data={user?.roles ?? []}
        handleDeleteRole={deleteRole}
        setShowModal={setShowModal}
      />
    </>
  );
};

export default UserManagementUsersEditDetailsPage;
